import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

// shadcn/ui button, restyled as NeuroForge's cut-corner neon buttons.
const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-display font-bold uppercase tracking-[0.14em] transition-[box-shadow,filter,background,color] duration-150 disabled:cursor-default disabled:opacity-35 active:translate-y-px [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'cut-sm bg-cyan text-ink hover:enabled:brightness-115 hover:enabled:drop-shadow-[0_0_6px_rgb(0_240_255/0.6)]',
        accent:
          'cut-sm bg-magenta text-ink hover:enabled:brightness-115 hover:enabled:drop-shadow-[0_0_6px_rgb(255_43_214/0.6)]',
        ghost:
          'bg-transparent text-cyan shadow-[inset_0_0_0_1px_var(--color-line-hot)] hover:enabled:bg-cyan/8 hover:enabled:shadow-[inset_0_0_0_1px_var(--color-cyan)]',
        danger:
          'bg-transparent text-red shadow-[inset_0_0_0_1px_var(--color-line-hot)] hover:enabled:bg-red/10 hover:enabled:shadow-[inset_0_0_0_1px_var(--color-red)]',
      },
      size: {
        default: 'px-3.5 py-1.5 text-[11px]',
        sm: 'px-2.5 py-1 text-[9px]',
        lg: 'px-6 py-3 text-xs',
        icon: 'size-8 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'button';
  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}
